import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, RefreshCw, RotateCcw, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { LedgerAccountAutocomplete } from './LedgerAccountAutocomplete';
import { AlertBanner, Field, SectionCard, inputClass } from './accountingUi';
import { formatMoney, getFinancialYearId, parseKendoResult } from '../../utils/accountingHelpers';
import {
  buildKendoDateRange,
  buildProcessPdcPayload,
  formatReportDate,
} from '../../utils/accountingReportHelpers';
import { orderDateToInputValue } from '../../utils/dateLocal';

const todayInput = () => orderDateToInputValue(new Date());

const PdcList = () => {
  const { mode } = useParams();
  const isPending = mode !== 'processed';
  const title = isPending ? 'Pending PDC' : 'Processed PDC';

  const [dateFrom, setDateFrom] = useState(todayInput);
  const [dateTo, setDateTo] = useState(todayInput);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionRow, setActionRow] = useState(null);
  const [actionStatus, setActionStatus] = useState('Clear');
  const [reconcileDate, setReconcileDate] = useState(todayInput);
  const [bankAccount, setBankAccount] = useState(null);
  const [processing, setProcessing] = useState(false);

  const loadRows = useCallback(async () => {
    const financialYearId = getFinancialYearId();
    if (!financialYearId) {
      setError('Financial year is not set. Log out and log in again.');
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.PDCsInView({
        ...buildKendoDateRange(new Date(dateFrom), new Date(dateTo)),
        PDCType: isPending ? 'Pending' : 'Processed',
        FinancialYearId: financialYearId,
        page: 1,
        pageSize: 500,
        skip: 0,
        take: 500,
      });
      const { rows: data } = parseKendoResult(response);
      setRows(data);
    } catch (err) {
      setError(err?.message || 'Failed to load PDC records');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, isPending]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openProcessModal = (row, status) => {
    setActionRow(row);
    setActionStatus(status);
    setReconcileDate(todayInput());
    setBankAccount(
      row.ReconcileBankAccount?.Id
        ? row.ReconcileBankAccount
        : row.BankAccount?.Id
          ? row.BankAccount
          : null
    );
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    setActionRow(null);
    setBankAccount(null);
  };

  const handleProcess = async () => {
    if (!actionRow) return;
    if (actionStatus === 'Clear' && !bankAccount?.Id) {
      setError('Select a bank account to clear this cheque.');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      const payload = buildProcessPdcPayload(
        actionRow,
        new Date(reconcileDate),
        actionStatus,
        bankAccount?.Id
      );
      const result = await MainService.ProcessPDC(payload);
      if (Number(result) === 1) {
        setSuccess(`PDC ${actionStatus === 'Clear' ? 'cleared' : 'returned'} successfully.`);
        closeModal();
        loadRows();
      } else if (Number(result) === 2) {
        setError('This PDC was already processed. Refresh and try again.');
      } else {
        setError('Failed to process PDC.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to process PDC');
    } finally {
      setProcessing(false);
    }
  };

  const handleReverse = async (row) => {
    if (!window.confirm(`Reverse processed PDC ${row.PDCNo || row.Id}?`)) return;
    try {
      setProcessing(true);
      setError('');
      const ok = await MainService.ReversePDC({
        Id: row.Id,
        FinancialYearId: getFinancialYearId(),
      });
      if (ok) {
        setSuccess('PDC reversed successfully.');
        loadRows();
      } else {
        setError('Failed to reverse PDC.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to reverse PDC');
    } finally {
      setProcessing(false);
    }
  };

  const columns = useMemo(() => {
    const base = [
      { key: 'PDCNo', label: 'PDC No' },
      { key: 'VoucherNo', label: 'Voucher No' },
      { key: 'Type', label: 'Type' },
      { key: 'ChequeNo', label: 'Cheque No' },
      { key: 'Date', label: 'Date', format: formatReportDate },
      { key: 'ChequeDate', label: 'Cheque Date', format: formatReportDate },
      { key: 'Amount', label: 'Amount', align: 'right', format: (v) => formatMoney(v) },
      { key: 'AccountName', label: 'PDC Account' },
      { key: 'DetailAccountName', label: 'Party Account' },
      { key: 'Status', label: 'Status' },
    ];
    if (!isPending) {
      base.splice(6, 0, {
        key: 'ReconcileDate',
        label: 'Reconcile Date',
        format: formatReportDate,
      });
      base.push({
        key: 'ReconcileBankAccount',
        label: 'Bank Account',
        format: (_, row) => row.ReconcileBankAccount?.Name || row.BankAccountName || '-',
      });
    }
    return base;
  }, [isPending]);

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">
            {isPending
              ? 'PDC records created from cheque payment and receipt vouchers.'
              : 'Cleared or returned PDC records.'}
          </p>
        </div>
        <button
          type="button"
          onClick={loadRows}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <AlertBanner message={error} />
      <AlertBanner type="success" message={success} />

      <SectionCard title="Filters">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Field label="Date from">
            <input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="Date to">
            <input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <div className="flex items-end">
            <button
              type="button"
              onClick={loadRows}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
            >
              <CheckCircle className="h-4 w-4" />
              Apply
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="PDC list" bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className={`px-3 py-3 text-left ${col.align === 'right' ? 'text-right' : ''}`}>
                    {col.label}
                  </th>
                ))}
                <th className="px-3 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-gray-500">
                    Loading PDC records...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-gray-500">
                    No PDC records found for the selected period.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.Id} className="hover:bg-gray-50">
                    {columns.map((col) => {
                      const raw = col.key === 'ReconcileBankAccount' ? null : row[col.key];
                      const value = col.format ? col.format(raw, row) : raw || '-';
                      return (
                        <td
                          key={col.key}
                          className={`px-3 py-2 text-gray-800 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                        >
                          {value}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-center gap-2">
                        {isPending ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openProcessModal(row, 'Clear')}
                              className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              onClick={() => openProcessModal(row, 'Return')}
                              className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
                            >
                              Return
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleReverse(row)}
                            disabled={processing}
                            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Reverse
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {actionRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="text-base font-semibold text-gray-900">
                {actionStatus === 'Clear' ? 'Clear cheque' : 'Return cheque'}
              </h3>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <p className="text-sm text-gray-600">
                PDC <strong>{actionRow.PDCNo}</strong> · Cheque <strong>{actionRow.ChequeNo || '-'}</strong> ·{' '}
                {formatMoney(actionRow.Amount)}
              </p>
              <Field label="Reconcile date">
                <input
                  type="date"
                  className={inputClass}
                  value={reconcileDate}
                  onChange={(e) => setReconcileDate(e.target.value)}
                />
              </Field>
              {actionStatus === 'Clear' ? (
                <Field label="Bank account">
                  <LedgerAccountAutocomplete
                    source="bank"
                    placeholder="Select bank account"
                    value={bankAccount}
                    onSelect={setBankAccount}
                  />
                </Field>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProcess}
                disabled={processing}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
              >
                {processing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PdcList;
